import { useEffect, useRef, useState } from 'react';

interface StatsCardProps {
  title: string;
  value: string | number;
  icon?: React.ReactNode;
  color?: 'blue' | 'green' | 'yellow' | 'purple' | 'orange' | 'red' | 'luxury';
  delay?: number;
}

const colorClasses = {
  blue: {
    bg: 'bg-blue-500/20',
    text: 'text-blue-400',
    border: 'border-blue-500/50',
    iconBg: 'bg-blue-500/10',
  },
  green: {
    bg: 'bg-green-500/20',
    text: 'text-green-400',
    border: 'border-green-500/50',
    iconBg: 'bg-green-500/10',
  },
  yellow: {
    bg: 'bg-yellow-500/20',
    text: 'text-yellow-400',
    border: 'border-yellow-500/50',
    iconBg: 'bg-yellow-500/10',
  },
  purple: {
    bg: 'bg-purple-500/20',
    text: 'text-purple-400',
    border: 'border-purple-500/50',
    iconBg: 'bg-purple-500/10',
  },
  orange: {
    bg: 'bg-orange-500/20',
    text: 'text-orange-400',
    border: 'border-orange-500/50',
    iconBg: 'bg-orange-500/10',
  },
  red: {
    bg: 'bg-red-500/20',
    text: 'text-red-400',
    border: 'border-red-500/50',
    iconBg: 'bg-red-500/10',
  },
  luxury: {
    bg: 'bg-brand-luxury/15',
    text: 'text-brand-luxury drop-shadow-[0_0_3px_rgba(230,161,87,0.2)]',
    border: 'border-brand-luxury/40',
    iconBg: 'bg-brand-luxury/8',
  },
};

export default function StatsCard({ title, value, icon, color = 'blue', delay = 0 }: StatsCardProps) {
  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, delay);

    return () => clearTimeout(timer);
  }, [delay]);

  const colorClass = colorClasses[color];

  return (
    <div
      ref={cardRef}
      className={`bg-neutral-dark/50 backdrop-blur-lg rounded-lg p-6 border ${
        colorClass.border
      } transition-all duration-500 ease-out hover:border-brand-accent/50 hover:shadow-lg hover:shadow-brand-accent/10 hover:-translate-y-1 ${
        isVisible
          ? 'opacity-100 translate-y-0'
          : 'opacity-0 translate-y-4'
      }`}
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-body text-neutral-medium uppercase tracking-wider">{title}</h3>
        {icon && (
          <div
            className={`w-12 h-12 rounded-lg flex items-center justify-center ${colorClass.iconBg} ${colorClass.text} transition-transform duration-300 hover:scale-110`}
          >
            {icon}
          </div>
        )}
      </div>
      <p className={`text-3xl font-heading font-bold ${colorClass.text} transition-all duration-300`}>
        {value}
      </p>
    </div>
  );
}
